import { useMemo } from 'react';
import { X, CreditCard, Phone, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ImagePreview } from '@/components/ImagePreview';
import { StatusBadge, getStatusLabel, type Status } from '@/components/StatusBadge';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useUserPaymentsQuery } from '@/api/query/payment';
import { usePaymentContext } from '@/contexts/PaymentContext';
import type { ViewPaymentProps } from '@/types';
import {
  formatCurrency,
  formatPaymentDate,
  getCurrentMonthLabel,
  sortPaymentsByDateDesc,
} from '@/lib/payments';
import {
  getLifeStatus,
  getMembershipStatus,
  getPaymentMonthStatus,
  isAddPaymentDisabled,
  isPaymentClosed,
  SUCCESS_NOTICE_CLASS,
  SUCCESS_NOTICE_ACCENT_CLASS,
  SUCCESS_AMOUNT_CLASS,
} from '@/lib/utils';

const DRAWER_CLASS =
  'h-full !w-full !min-w-0 !max-w-full overflow-hidden flex flex-col sm:!w-[45vw] sm:!min-w-[650px] sm:!max-w-[45vw]';

const MONTH_STATUS_DOT: Record<string, string> = {
  paid: 'bg-teal-500',
  unpaid: 'bg-amber-500',
  no_dues: 'bg-teal-500',
  closed: 'bg-gray-400',
};

export function ViewPayment({ member, open, onOpenChange, onAddPayment }: ViewPaymentProps) {
  const { getManagementByMemberId, isManagementLoading } = usePaymentContext();

  const {
    data: userPayments = [],
    isLoading: isPaymentsLoading,
    isError: isPaymentsError,
  } = useUserPaymentsQuery(member?.id, open && Boolean(member?.id));

  const payments = useMemo(() => sortPaymentsByDateDesc(userPayments), [userPayments]);

  const managementItem = member ? getManagementByMemberId(member.id) : undefined;
  const isSummaryLoading = isManagementLoading && !managementItem;
  const monthsPaid = managementItem?.paid_months ?? 0;
  const totalMonths = managementItem?.total_months ?? 100;
  const summary = {
    totalPaid: managementItem?.total_paid ?? 0,
    amountDue: managementItem?.due_this_today ?? 0,
    monthsPaid,
    monthsRemaining: Math.max(0, totalMonths - monthsPaid),
    paymentProgress: Math.min(totalMonths, monthsPaid),
  };
  const paidThisMonth = managementItem?.this_month?.toLowerCase() === 'paid';
  const paymentTotals = useMemo(() => {
    const cash = payments
      .filter((payment) => payment.paymentType === 'Cash')
      .reduce((sum, payment) => sum + payment.amount, 0);
    const online = payments
      .filter((payment) => payment.paymentType === 'Online')
      .reduce((sum, payment) => sum + payment.amount, 0);
    return { cash, online, total: cash + online };
  }, [payments]);
  const membershipStatus = useMemo(
    () => (member ? getMembershipStatus(member, monthsPaid) : 'active'),
    [member, monthsPaid]
  );
  const paymentMonthStatus = useMemo(
    () => (member ? getPaymentMonthStatus(member, paidThisMonth, monthsPaid) : 'unpaid'),
    [member, paidThisMonth, monthsPaid]
  );
  const accountClosed = member ? isPaymentClosed(member) : false;
  const noDues = paymentMonthStatus === 'no_dues';
  const addPaymentDisabled = member ? isAddPaymentDisabled(member, monthsPaid) : false;

  const handleClose = () => onOpenChange(false);

  const handleAddPayment = () => {
    if (!member || !onAddPayment || addPaymentDisabled) return;
    onAddPayment(member);
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="right">
      <DrawerContent className={DRAWER_CLASS}>
        <DrawerHeader className="shrink-0 border-b border-gray-200 bg-white px-6 py-5">
          <div className="flex items-center justify-between gap-4">
            <DrawerTitle className="text-base font-semibold text-gray-900">
              Payment Details
            </DrawerTitle>
            <Tooltip>
              <TooltipTrigger asChild>
                <DrawerClose asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0 rounded-md border border-gray-200 text-gray-500 hover:border-[#4369a0]/30 hover:bg-[#4369a0]/10 hover:text-[#4369a0]"
                  >
                    <X className="h-3.5 w-3.5" />
                    <span className="sr-only">Close</span>
                  </Button>
                </DrawerClose>
              </TooltipTrigger>
              <TooltipContent side="bottom">Close</TooltipContent>
            </Tooltip>
          </div>
        </DrawerHeader>

        <div className="min-h-0 flex-1 overflow-y-auto scrollbar-hide bg-gray-50/80 px-6 py-5">
          {member && (
            <div className="space-y-5">
              {accountClosed && (
                <div className="rounded-lg border border-gray-300 bg-gray-100 px-4 py-3 text-sm text-gray-700">
                  This member account is <span className="font-semibold">Closed</span>. New payments
                  cannot be added.
                </div>
              )}

              {noDues && (
                <div className={SUCCESS_NOTICE_CLASS}>
                  All <span className={SUCCESS_NOTICE_ACCENT_CLASS}>100 months</span> are paid.{' '}
                  <span className={SUCCESS_NOTICE_ACCENT_CLASS}>No dues</span> remaining — new
                  payments cannot be added.
                </div>
              )}

              <div className="rounded-xl border border-gray-200/80 bg-white px-5 py-5">
                <div className="flex items-start gap-3.5">
                  <ImagePreview src={member.profileImage || member.photo} alt={member.name || 'Member photo'}>
                  <Avatar className="h-11 w-11 shrink-0">
                    <AvatarImage src={member.profileImage || member.photo} />
                    <AvatarFallback className="bg-gray-100 text-xs font-semibold tracking-wide text-gray-600">
                      {member.name
                        .split(' ')
                        .map((n) => n[0])
                        .join('')}
                    </AvatarFallback>
                  </Avatar>
                  </ImagePreview>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
                      <h2 className="truncate text-base font-semibold tracking-tight text-gray-900">
                        {member.name}
                      </h2>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <StatusBadge status={membershipStatus} />
                        <StatusBadge status={getLifeStatus(member)} />
                      </div>
                    </div>

                    <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[13px] text-gray-500">
                      <span className="tabular-nums">#{member.id}</span>
                      <span className="text-gray-300" aria-hidden>
                        ·
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Phone className="h-3 w-3 text-gray-400" />
                        +91 {member.phone}
                      </span>
                    </p>

                    <p className="mt-2.5 flex flex-wrap items-center gap-2 text-[13px]">
                      {isSummaryLoading ? (
                        <span className="text-gray-400">Loading status…</span>
                      ) : (
                        <>
                          <span
                            className={`h-1.5 w-1.5 shrink-0 rounded-full ${MONTH_STATUS_DOT[paymentMonthStatus] ?? 'bg-gray-400'}`}
                            aria-hidden
                          />
                          <span className="font-medium text-gray-800">
                            {getStatusLabel(paymentMonthStatus)}
                          </span>
                          {(paymentMonthStatus === 'paid' || paymentMonthStatus === 'unpaid') && (
                            <>
                              <span className="text-gray-300" aria-hidden>
                                ·
                              </span>
                              <span className="text-gray-500">{getCurrentMonthLabel()}</span>
                            </>
                          )}
                        </>
                      )}
                    </p>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-3 gap-4 border-t border-gray-100 pt-5">
                  <div>
                    <p className="text-[20px] font-semibold tabular-nums tracking-tight text-gray-900">
                      {isSummaryLoading ? '—' : formatCurrency(summary.totalPaid)}
                    </p>
                    <p className="mt-0.5 text-[12px] text-gray-500">Total paid</p>
                  </div>
                  <div>
                    <p className="text-[20px] font-semibold tabular-nums tracking-tight text-gray-900">
                      {isSummaryLoading ? '—' : formatCurrency(summary.amountDue)}
                    </p>
                    <p className="mt-0.5 text-[12px] text-gray-500">Remaining</p>
                  </div>
                  <div>
                    <p className="text-[20px] font-semibold tabular-nums tracking-tight text-gray-900">
                      {isSummaryLoading ? '—' : summary.monthsRemaining}
                    </p>
                    <p className="mt-0.5 text-[12px] text-gray-500">Months left</p>
                  </div>
                </div>

                <div className="mt-5">
                  <div className="mb-1.5 flex items-center justify-between text-[12px]">
                    <span className="text-gray-500">Progress</span>
                    <span className="tabular-nums text-gray-600">
                      {isSummaryLoading
                        ? '—'
                        : `${summary.paymentProgress} of 100 months`}
                    </span>
                  </div>
                  <div className="h-1 overflow-hidden rounded-full bg-gray-100">
                    <div
                      className="h-full rounded-full bg-[#4369a0] transition-all"
                      style={{
                        width: isSummaryLoading ? '0%' : `${summary.paymentProgress}%`,
                      }}
                    />
                  </div>
                </div>
              </div>

              <section className="overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-sm">
                <div className="border-b border-gray-100 px-5 py-4">
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <CreditCard className="h-4 w-4 text-[#4369a0]" />
                    Payment History
                    {!isPaymentsLoading && (
                      <span className="ml-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                        {payments.length}
                      </span>
                    )}
                  </h3>
                </div>
                <div className="p-5">
                  {isPaymentsLoading ? (
                    <div className="flex flex-col items-center justify-center rounded-lg border border-gray-200/80 bg-gray-50/50 py-12">
                      <span className="h-8 w-8 animate-spin rounded-full border-2 border-[#4369a0]/30 border-t-[#4369a0]" />
                      <p className="mt-3 text-sm text-gray-500">Loading payment history...</p>
                    </div>
                  ) : isPaymentsError ? (
                    <div className="rounded-lg border border-red-200 bg-red-50/60 py-12 text-center">
                      <p className="text-sm font-medium text-red-700">
                        Unable to load payment history
                      </p>
                      <p className="mt-1 text-sm text-red-600/80">Please try again.</p>
                    </div>
                  ) : payments.length > 0 ? (
                    <>
                      <div className="mb-4 rounded-lg border border-gray-200/80 bg-gray-50/60 p-4">
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Total paid
                        </p>
                        <div className="space-y-1.5 text-sm">
                          <div className="flex items-center justify-between gap-4">
                            <span className="text-gray-600">Online</span>
                            <span className="font-medium tabular-nums text-gray-900">
                              {formatCurrency(paymentTotals.online)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between gap-4">
                            <span className="text-gray-600">Cash</span>
                            <span className="font-medium tabular-nums text-gray-900">
                              {formatCurrency(paymentTotals.cash)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between gap-4 border-t border-gray-200 pt-2">
                            <span className="font-semibold text-gray-900">Total</span>
                            <span className={SUCCESS_AMOUNT_CLASS}>
                              {formatCurrency(paymentTotals.total)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="overflow-hidden rounded-lg border border-gray-200/80">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-gray-200 bg-gray-50/80 hover:bg-gray-50/80">
                            <TableHead className="h-10 text-xs font-semibold tracking-wide text-gray-800">
                              Date
                            </TableHead>
                            <TableHead className="h-10 text-xs font-semibold tracking-wide text-gray-800">
                              Period
                            </TableHead>
                            <TableHead className="h-10 text-xs font-semibold tracking-wide text-gray-800">
                              Amount
                            </TableHead>
                            <TableHead className="h-10 text-right text-xs font-semibold tracking-wide text-gray-800">
                              Type
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {payments.map((payment) => (
                            <TableRow key={payment.id} className="border-gray-100">
                              <TableCell className="text-sm text-gray-700">
                                {formatPaymentDate(payment.paymentDate)}
                              </TableCell>
                              <TableCell className="text-sm text-gray-600">
                                {payment.dueStart && payment.dueEnd && payment.dueStart !== payment.dueEnd
                                  ? `${payment.dueStart} – ${payment.dueEnd}`
                                  : payment.dueStart
                                    ? payment.dueStart
                                    : `${payment.month} ${payment.year}`}
                              </TableCell>
                              <TableCell className="text-sm tabular-nums text-gray-900">
                                {formatCurrency(payment.amount)}
                              </TableCell>
                              <TableCell className="text-right">
                                <StatusBadge status={payment.paymentType.toLowerCase() as Status} />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    </>
                  ) : (
                    <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/50 py-12 text-center">
                      <CreditCard className="mx-auto mb-2 h-8 w-8 text-gray-300" />
                      <p className="text-sm font-medium text-gray-600">No payments added yet</p>
                      <p className="mt-1 text-sm text-gray-500">
                        Add the first payment for this member.
                      </p>
                    </div>
                  )}
                </div>
              </section>
            </div>
          )}
        </div>

        {onAddPayment && (
          <DrawerFooter className="shrink-0 flex-row justify-end gap-2 border-t border-gray-200 bg-white px-6 py-4">
            <Button type="button" variant="outline" onClick={handleClose} className="border-gray-200">
              Close
            </Button>
            <Button
              type="button"
              onClick={handleAddPayment}
              disabled={addPaymentDisabled}
              className="min-w-36 bg-[#4369a0] text-white hover:bg-[#4369a0]/90 disabled:opacity-60"
            >
              Add Payment
            </Button>
          </DrawerFooter>
        )}
      </DrawerContent>
    </Drawer>
  );
}
